import { cn } from "@/lib/utils";
import { LucideIcon } from "lucide-react";
import { ComponentType } from "react";

/**
 * IconBackground Component
 * 
 * Displays an icon inside a blob-shaped gradient background.
 * Part of the LEAP design system for decorative icon treatments.
 * Supports both Lucide and Tabler icons.
 * 
 * @param icon - Icon component to render (Lucide or Tabler)
 * @param gradient - Brand colour gradient (teal, purple, orange, green, yellow, red, green-purple, pink-purple)
 * @param size - Size variant (sm: 12px, md: 16px, lg: 20px)
 * @param className - Additional CSS classes
 * 
 * @example
 * <IconBackground icon={Rocket} gradient="teal" size="md" />
 */
interface IconBackgroundProps {
  icon: LucideIcon | ComponentType<any>;
  gradient?: "teal" | "purple" | "orange" | "green" | "yellow" | "red" | "green-purple" | "pink-purple" | "blue" | "slate" | "pink";
  size?: "sm" | "md" | "lg";
  className?: string;
}

export const IconBackground = ({ 
  icon: Icon, 
  gradient = "teal",
  size = "md",
  className 
}: IconBackgroundProps) => {
  const sizeClasses = {
    sm: "w-12 h-12",
    md: "w-16 h-16",
    lg: "w-20 h-20",
  };

  const iconSizes = {
    sm: 20,
    md: 28,
    lg: 36,
  };

  const gradientClasses = {
    teal: "bg-gradient-to-br from-brand-teal to-brand-teal-600",
    purple: "bg-gradient-to-br from-brand-purple to-brand-purple-600",
    orange: "bg-gradient-to-br from-brand-orange to-brand-orange-600",
    green: "bg-gradient-to-br from-brand-green to-brand-green-600",
    yellow: "bg-gradient-to-br from-brand-yellow to-brand-yellow-600",
    red: "bg-gradient-to-br from-brand-red to-brand-red-600",
    pink: "bg-gradient-to-br from-[hsl(330,100%,70%)] to-[hsl(330,100%,50%)]",
    "green-purple": "bg-gradient-to-br from-brand-green to-brand-purple",
    "pink-purple": "bg-gradient-to-br from-[hsl(330,100%,60%)] to-brand-purple",
    blue: "bg-gradient-to-br from-[hsl(210,100%,60%)] to-[hsl(210,100%,50%)]",
    slate: "bg-gradient-to-br from-[hsl(215,25%,50%)] to-[hsl(215,25%,40%)]",
  };

  return (
    <div 
      className={cn(
        sizeClasses[size],
        gradientClasses[gradient],
        "rounded-[32%_68%_65%_35%_/_45%_38%_62%_55%]", // Blob shape
        "flex items-center justify-center",
        "shadow-lg transition-all duration-150",
        className
      )}
    >
      <Icon 
        size={iconSizes[size]} 
        className="text-white" 
        strokeWidth={2.5}
      />
    </div>
  );
};
